import React from 'react';
import { LiveKnowledgeSearchResult } from '../services/apiService';
import { ALERT_BADGE_LABEL, AlertPalette, COMPETITOR_PALETTE, LANDMINE_PALETTE } from '../constants/alertTheme';
import { ConversationCategory } from '../types/conversation';
import { AlertBulletList, AlertParagraph, AlertSection, AlertShell } from './alerts/AlertShell';
import { AnswerText, CategoryChip } from './InsightCardParts';

type ResultOf<T extends LiveKnowledgeSearchResult['type']> = Extract<LiveKnowledgeSearchResult, { type: T }>;

/** A text field of the response, trimmed; anything else is empty. The response is external input. */
const guidanceText = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

/** A list of the response's guidance points (strings, or `{ point }` as the live alerts get them), blanks dropped. */
const guidancePoints = (values: unknown): string[] => (Array.isArray(values) ? values : [])
  .map((value) => guidanceText(typeof value === 'string' ? value : value?.point))
  .filter(Boolean);

/**
 * The flat `answer`, for a result whose backend sends no `details`: one
 * section, its lines kept as the backend wrote them.
 */
const FallbackSection: React.FC<{ palette: AlertPalette; title: string; answer: string }> = ({ palette, title, answer }) => (
  <AlertSection palette={palette} title={title}>
    <div className="whitespace-pre-line">
      <AlertParagraph palette={palette}>{answer}</AlertParagraph>
    </div>
  </AlertSection>
);

/**
 * An objection or FAQ: the live insight card's category chip, question box and
 * stored-answer box (white card, radius 16, padding 12, blocks 8 apart). No
 * thumbs, status dot, relevance, edit pencil or answer tabs: nothing here was
 * said on the call.
 */
const InsightResultCard: React.FC<{ result: ResultOf<'objection' | 'faq'> }> = ({ result }) => (
  <article className="space-y-2 rounded-card border border-hairline bg-surface p-3">
    <div className="flex">
      <CategoryChip category={result.type === 'faq' ? ConversationCategory.FAQ : ConversationCategory.OBJECTION} />
    </div>
    <div className="rounded-control bg-paper p-2.5">
      <h4 className="text-sm font-bold text-ink">{result.label}</h4>
    </div>
    {result.answer?.trim() && (
      <div className="rounded-control bg-paper p-2.5 text-sm text-ink">
        <AnswerText answer={result.answer} />
      </div>
    )}
  </article>
);

/**
 * A landmine: the live landmine alert's card (same palette, shell and
 * sections) named by its trigger. No speaker, trigger quote, thumbs or close,
 * and never collapsed: nobody said it.
 */
const LandmineResultCard: React.FC<{ result: ResultOf<'landmine'> }> = ({ result }) => {
  const quickTips = guidanceText(result.details?.quickTips);
  const canSay = guidancePoints(result.details?.canSay);
  const cannotSay = guidancePoints(result.details?.cannotSay);
  const hasDetails = Boolean(quickTips) || canSay.length > 0 || cannotSay.length > 0;
  const fallback = hasDetails ? '' : guidanceText(result.answer);

  return (
    <AlertShell palette={LANDMINE_PALETTE} badgeLabel={ALERT_BADGE_LABEL.landmine} name={result.label} spacingClassName="">
      {fallback && <FallbackSection palette={LANDMINE_PALETTE} title="Quick Tips" answer={fallback} />}
      {quickTips && (
        <AlertSection palette={LANDMINE_PALETTE} title="Quick Tips">
          <AlertParagraph palette={LANDMINE_PALETTE}>{quickTips}</AlertParagraph>
        </AlertSection>
      )}
      {canSay.length > 0 && (
        <AlertSection palette={LANDMINE_PALETTE} title="You can say">
          <AlertBulletList palette={LANDMINE_PALETTE} items={canSay} />
        </AlertSection>
      )}
      {cannotSay.length > 0 && (
        <AlertSection palette={LANDMINE_PALETTE} title="You cannot say">
          <AlertBulletList palette={LANDMINE_PALETTE} items={cannotSay} marker="cross" />
        </AlertSection>
      )}
    </AlertShell>
  );
};

/**
 * A competitor: the live competitor alert's card (same palette, shell and
 * sections) named by the competitor, its sections open (no "Show details").
 */
const CompetitorResultCard: React.FC<{ result: ResultOf<'competitor'> }> = ({ result }) => {
  const talkingPoints = guidanceText(result.details?.talkingPoints);
  const advantages = guidancePoints(result.details?.advantages);
  const weaknesses = guidancePoints(result.details?.weaknesses);
  const hasDetails = Boolean(talkingPoints) || advantages.length > 0 || weaknesses.length > 0;
  const fallback = hasDetails ? '' : guidanceText(result.answer);

  return (
    <AlertShell palette={COMPETITOR_PALETTE} badgeLabel={ALERT_BADGE_LABEL.competitor} name={result.label} spacingClassName="">
      {fallback && <FallbackSection palette={COMPETITOR_PALETTE} title="Talking Points" answer={fallback} />}
      {talkingPoints && (
        <AlertSection palette={COMPETITOR_PALETTE} title="Talking Points">
          <AlertParagraph palette={COMPETITOR_PALETTE}>{talkingPoints}</AlertParagraph>
        </AlertSection>
      )}
      {advantages.length > 0 && (
        <AlertSection palette={COMPETITOR_PALETTE} title="Our advantages">
          <AlertBulletList palette={COMPETITOR_PALETTE} items={advantages} />
        </AlertSection>
      )}
      {weaknesses.length > 0 && (
        <AlertSection palette={COMPETITOR_PALETTE} title={`Where ${result.label || 'they'} fall short`}>
          <AlertBulletList palette={COMPETITOR_PALETTE} items={weaknesses} />
        </AlertSection>
      )}
    </AlertShell>
  );
};

/** One Knowledge Search answer, drawn as the live tool draws that kind of guidance. Same on macOS and WPF. */
const KnowledgeResultCard: React.FC<{ result: LiveKnowledgeSearchResult }> = ({ result }) => {
  switch (result.type) {
    case 'landmine':
      return <LandmineResultCard result={result} />;
    case 'competitor':
      return <CompetitorResultCard result={result} />;
    default:
      return <InsightResultCard result={result} />;
  }
};

export default KnowledgeResultCard;
